/**
 * Copyright (c) 2020-2021 Perficient, Inc.
 * Unauthorized copying or distribution of this file prohibited without the express permission of Perficient.
 */

import React from "react";
import { CardContent } from "@material-ui/core";
import FormTextField from "../../../components/FormTextField/FormTextField";
import { ConfirmDialog } from "../../../components/ConfirmDialog/ConfirmDialog";
import { CANNED_RESPONSE_TOOLTIPS } from "../../../utility/toolTipMessages";
import errorCodes from "../../../utility/errorCodes";
import { ResponseList } from "./ResponseList";
import { v2, errorToObject } from "@prftesp/cle-logger";
import { v4 as uuid } from "uuid";
import { ActionButtons } from "../../../components/Common";
import EmptyPlaceholder from "../../../components/EmptyPlaceholder/EmptyPlaceholder";

const COMPONENT_NAME = "Admin responseManager/components/CategoryDetails";

const getNameErrorMessage = (categories, value) => {
	if (
		categories?.filter(
			(x) =>
				(x.categoryName === value.categoryName || x.categoryName.toLowerCase() === value.categoryName.toLowerCase()) &&
				x.categoryId !== value.categoryId,
		).length > 0
	) {
		return "Category name already exists";
	}
	if (!value.categoryName) {
		return "Category name is required.";
	}

	if (value.categoryName.length > 50) {
		return "Category name cannot exceed 50 characters.";
	}
};

const validateCategory = (category, categories, formEdited) => {
	if (!formEdited) {
		return false;
	}
	if (
		categories?.filter(
			(x) =>
				(x.categoryName === category.categoryName ||
					x.categoryName.toLowerCase() === category.categoryName.toLowerCase()) &&
				x.categoryId !== category.categoryId,
		).length > 0
	) {
		return false;
	}
	if (!category.categoryName || category.categoryName.length > 50) {
		return false;
	}

	if (!category.responses || !category.responses.length) {
		return false;
	}

	for (let index = 0; index < category.responses.length; index++) {
		const response = category.responses[index];
		if (!response.responseName || response.responseName.length > 50) {
			return false;
		}
		if (!response.message) {
			return false;
		}
	}

	return true;
};

const newResponseObject = () => ({
	responseId: uuid(),
	responseName: "",
	tags: [],
	message: "",
});
/**
 * Details of the canned response category
 */
export const CategoryDetails = ({
	categories,
	category,
	removeCategoryHandler,
	saveCategoryHandler,
	cancelHandler,
	formEdited,
	setFormEdited,
}) => {
	const [editableCategory, setEditableCategory] = React.useState(null);
	const [removeConfirmOpen, setRemoveConfirmOpen] = React.useState(false);
	React.useEffect(() => {
		if (category) {
			setFormEdited(false);
			setEditableCategory({ ...category });
		} else {
			setEditableCategory(null);
			setFormEdited(false);
		}
	}, [category, setFormEdited]);

	const updatePropertyHandler = React.useCallback(
		(property, value) => {
			setEditableCategory({ ...editableCategory, [property]: value });
		},
		[editableCategory],
	);

	const removeResponse = (index) => {
		const newResponses = [...editableCategory.responses];
		newResponses.splice(index, 1);
		setEditableCategory({
			...editableCategory,
			responses: newResponses,
		});
		setFormEdited(true);
	};

	const isCategoryValid = editableCategory && validateCategory(editableCategory, categories, formEdited);

	return (
		<div>
			{!editableCategory ? (
				<EmptyPlaceholder
					message="To get started, select a category from the list on the left, or click the 'Add' button to create a new
									category."
				/>
			) : (
				<>
					<CardContent>
						<div className="d-flex align-items-center justify-content-between flex-wrap">
							<h5 style={{ fontWeight: "bold", margin: 0 }}>
								{editableCategory.new ? "Add Category" : "Edit Category"}
							</h5>
							<ActionButtons
								showCancel
								className="d-flex my-2 my-md-0"
								onCancel={cancelHandler}
								showRemove={!editableCategory.new}
								onRemove={() => setRemoveConfirmOpen(true)}
								showSave={formEdited}
								onSave={() => saveCategoryHandler(editableCategory)}
								disableSave={!isCategoryValid}
							/>
						</div>
						<div className="mt-4 d-flex align-items-center justify-content-between">
							<FormTextField
								autoFocus
								label={"Category"}
								formEdited={formEdited}
								required
								errorMessage={getNameErrorMessage(categories, editableCategory)}
								startingValue={editableCategory.categoryName}
								setConfigValue={updatePropertyHandler}
								setEdited={setFormEdited}
								valueName={"categoryName"}
								styleObject={{ flex: 1 }}
								fullWidth
								enableDebounce
								errorCheck={(val) =>
									!val ||
									val.length > 50 ||
									categories?.filter(
										(x) =>
											(x.categoryName === val || x.categoryName.toLowerCase() === val.toLowerCase()) &&
											x.categoryId !== editableCategory.categoryId,
									).length > 0
								}
								toolTipMessage={CANNED_RESPONSE_TOOLTIPS.CATEGORY_NAME}
							/>
						</div>
					</CardContent>
					<ResponseList
						responses={editableCategory.responses}
						removeResponseHandler={removeResponse}
						addResponseHandler={() =>
							updatePropertyHandler("responses", [newResponseObject(), ...editableCategory.responses])
						}
						updateResponseHandler={(updatedResponses) => updatePropertyHandler("responses", updatedResponses)}
						setFormEdited={setFormEdited}
						formEdited={formEdited}
					/>
					<ConfirmDialog
						title={"Remove Category?"}
						message={"Are you sure you want to delete the category and all related responses?"}
						confirmOpen={removeConfirmOpen}
						onClose={async (confirmed) => {
							try {
								if (confirmed) {
									removeCategoryHandler(editableCategory.categoryName, editableCategory.categoryId);
								}
								setRemoveConfirmOpen(false);
							} catch (error) {
								v2.error(`Error while removing canned response category.`, {
									additionalData: { error: errorToObject(error) },
									componentName: COMPONENT_NAME,
									logCode: errorCodes.DELETE_CATEGORY_API_ERROR,
								});
							}
						}}
					/>
				</>
			)}
		</div>
	);
};
